import React, { useEffect, useState } from "react";
import { Megaphone, Check, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatDateTime, formatErrorDetail } from "@/lib/api";
import { PageHeader, Empty } from "@/components/app/Primitives";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/lib/auth";
import { hasAnyRole } from "@/lib/perms";

const HR_ROLES = ["super_admin", "hr_admin", "hr_staff", "general_manager"];

export default function Announcements() {
  const { user, activeCompanyId } = useAuth();
  const canManage = hasAnyRole(user, activeCompanyId, HR_ROLES);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", kind: "announcement", requires_ack: false });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/announcements");
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatErrorDetail(e.response?.data?.detail) || "Failed to load");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    try {
      await api.post("/announcements", form);
      toast.success("Posted");
      setOpen(false);
      setForm({ title: "", body: "", kind: "announcement", requires_ack: false });
      load();
    } catch (e) {
      toast.error(formatErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const acknowledge = async (id) => {
    try {
      await api.post(`/announcements/${id}/acknowledge`, {});
      toast.success("Acknowledged");
      load();
    } catch (e) {
      toast.error(formatErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this announcement?")) return;
    try {
      await api.delete(`/announcements/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Announcements & Policies"
        subtitle="Broadcast company-wide announcements and require explicit acknowledgment for policy updates."
        actions={canManage && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button><Plus className="size-4 mr-1" /> New</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>New announcement</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>Title</Label>
                  <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
                </div>
                <div>
                  <Label>Body</Label>
                  <Textarea rows={6} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} />
                </div>
                <div>
                  <Label>Kind</Label>
                  <select className="w-full h-10 px-3 rounded-md border border-border"
                    value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
                    <option value="announcement">Announcement</option>
                    <option value="policy">Policy</option>
                    <option value="memo">Memo</option>
                  </select>
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.requires_ack}
                    onChange={(e) => setForm({ ...form, requires_ack: e.target.checked })} />
                  Require employee acknowledgment
                </label>
              </div>
              <DialogFooter><Button onClick={submit}>Post</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      />

      {loading && <div className="text-sm text-muted-foreground">Loading…</div>}

      {!loading && !items.length && (
        <Empty title="No announcements yet" subtitle="Once posted, they will appear here." />
      )}

      <div className="space-y-3">
        {items.map((a) => (
          <Card key={a.id}>
            <CardHeader className="flex flex-row items-start justify-between gap-2">
              <div>
                <CardTitle className="text-base flex items-center gap-2">
                  <Megaphone className="size-4" /> {a.title}
                </CardTitle>
                <div className="mt-1 flex items-center gap-2 flex-wrap text-xs text-muted-foreground">
                  <Badge variant="outline" className="capitalize">{a.kind}</Badge>
                  {a.requires_ack && <Badge>Requires Ack</Badge>}
                  <span>{formatDateTime(a.created_at)}</span>
                  {a.created_by_email && <span>by {a.created_by_email}</span>}
                  <span>· {a.ack_count} acknowledgment(s)</span>
                </div>
              </div>
              <div className="flex gap-1">
                {a.requires_ack && (a.acknowledged_by_me
                  ? <Badge variant="outline"><Check className="size-3 mr-1" /> Acknowledged</Badge>
                  : <Button size="sm" onClick={() => acknowledge(a.id)}><Check className="size-3 mr-1" /> Acknowledge</Button>
                )}
                {canManage && (
                  <Button size="sm" variant="outline" onClick={() => remove(a.id)}>
                    <Trash2 className="size-3" />
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-sm whitespace-pre-wrap">{a.body}</div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
